import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import buffer from '@turf/buffer';
import length from '@turf/length';
import area from '@turf/area';
import { lineString, point } from '@turf/helpers';
import { parseShapePoint } from '../utils/mapFilters';

const SHAPE_STYLE = {
  color: '#4786ed',
  weight: 2,
  fillColor: '#4786ed',
  fillOpacity: 0.2,
};

function buildCircleShape(layer) {
  const center = layer.getLatLng();
  const radiusMeters = layer.getRadius();
  const turfPoint = point([center.lng, center.lat]);
  const buffered = buffer(turfPoint, radiusMeters, { units: 'meters' });

  return {
    type: 'circle',
    geojson: buffered,
    metadata: {
      center: [center.lat, center.lng],
      radiusMeters,
    },
  };
}

function buildRectangleShape(layer) {
  const bounds = layer.getBounds();
  return {
    type: 'rectangle',
    geojson: layer.toGeoJSON(),
    metadata: {
      bounds: [
        [bounds.getSouth(), bounds.getWest()],
        [bounds.getNorth(), bounds.getEast()],
      ],
    },
  };
}

function buildPolygonShape(layer) {
  const geojson = layer.toGeoJSON();
  return {
    type: 'polygon',
    geojson,
    metadata: {
      vertexCount: geojson.geometry.coordinates[0].length - 1,
    },
  };
}

function buildBufferedLineShape(layer, bufferRadiusMeters) {
  const latLngs = layer.getLatLngs();
  const coords = latLngs.map(ll => [ll.lng, ll.lat]);
  const turfLine = lineString(coords);
  const buffered = buffer(turfLine, bufferRadiusMeters, { units: 'meters' });

  return {
    shape: {
      type: 'buffered-line',
      geojson: buffered,
      metadata: {
        originalLine: turfLine,
        bufferRadiusMeters,
      },
    },
    bufferedLayer: L.geoJSON(buffered, { style: () => SHAPE_STYLE }),
  };
}

function restoreShapeLayer(initialShape) {
  if (!initialShape) {
    return null;
  }

  const { shapeType, shapeRadius, shapePoints } = initialShape;
  const parsed = shapePoints.map(parseShapePoint).filter(Boolean);

  if (parsed.length === 0) {
    return null;
  }

  switch (shapeType) {
    case 'circle': {
      const [lat, lng] = parsed[0];
      const radius = shapeRadius ? parseFloat(shapeRadius) : 0;
      if (!radius) return null;
      return L.circle([lat, lng], { radius, ...SHAPE_STYLE });
    }

    case 'rectangle': {
      if (parsed.length < 2) return null;
      const [topLeft, bottomRight] = parsed;
      return L.rectangle(
        [
          [topLeft[0], topLeft[1]],
          [bottomRight[0], bottomRight[1]],
        ],
        SHAPE_STYLE,
      );
    }

    case 'polygon': {
      if (parsed.length < 3) return null;
      const latLngs = parsed.map(([lat, lng]) => [lat, lng]);
      return L.polygon(latLngs, SHAPE_STYLE);
    }

    default:
      return null;
  }
}

function makeHandler(map, tool) {
  switch (tool) {
    case 'polygon':
      return new L.Draw.Polygon(map, {
        allowIntersection: false,
        shapeOptions: SHAPE_STYLE,
      });
    case 'rectangle':
      return new L.Draw.Rectangle(map, { shapeOptions: SHAPE_STYLE });
    case 'circle':
      return new L.Draw.Circle(map, { shapeOptions: SHAPE_STYLE });
    case 'line':
      return new L.Draw.Polyline(map, {
        shapeOptions: { color: '#4786ed', weight: 3 },
        metric: true,
        feet: false,
        showLength: false,
      });
    default:
      return null;
  }
}

function formatLineLength(km) {
  return `${km.toFixed(2)} km`;
}

function formatAreaHectares(sqMeters) {
  return `${(sqMeters / 10000).toFixed(2)} ha`;
}

function formatRadiusMetres(metres) {
  return `Radius: ${Math.round(metres)} m`;
}

export default function DrawControl({
  activeTool,
  bufferRadius,
  onShapeDrawn,
  onToolEnd,
  onDrawProgress,
  isLayerUpdateRef,
  initialShape,
  drawControlRef,
}) {
  const map = useMap();
  const drawnItemsRef = useRef(null);
  const handlerRef = useRef(null);
  const bufferRadiusRef = useRef(bufferRadius);
  bufferRadiusRef.current = bufferRadius;
  const measureMarkerRef = useRef(null);

  useEffect(() => {
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    const restoredLayer = restoreShapeLayer(initialShape);
    if (restoredLayer) {
      drawnItems.addLayer(restoredLayer);
    }

    const handleDrawCreated = e => {
      drawnItems.clearLayers();

      const { layerType, layer } = e;
      let shape;

      switch (layerType) {
        case 'circle':
          drawnItems.addLayer(layer);
          shape = buildCircleShape(layer);
          break;

        case 'rectangle':
          drawnItems.addLayer(layer);
          shape = buildRectangleShape(layer);
          break;

        case 'polygon':
          drawnItems.addLayer(layer);
          shape = buildPolygonShape(layer);
          break;

        case 'polyline': {
          const result = buildBufferedLineShape(layer, bufferRadiusRef.current);
          drawnItems.addLayer(result.bufferedLayer);
          shape = result.shape;
          break;
        }

        default:
          return;
      }

      onShapeDrawn(shape);
    };

    const handleDrawStart = () => {
      if (isLayerUpdateRef) isLayerUpdateRef.current = true;
    };

    const handleDrawStop = () => {
      if (isLayerUpdateRef) isLayerUpdateRef.current = false;
    };

    // DRAWVERTEX fires for line/polygon as each point is placed. e.layers are
    // the vertex *markers* (getLatLng, not getLatLngs), so read the points from
    // the active handler's marker list instead.
    const handleDrawVertex = () => {
      const handler = handlerRef.current;
      if (!handler || !onDrawProgress) return;

      const markers = handler._markers || [];
      const count = markers.length;
      const coords = markers.map(m => {
        const ll = m.getLatLng();
        return [ll.lng, ll.lat];
      });

      let measurement = null;
      if (handler.type === 'polyline' && count >= 2) {
        measurement = formatLineLength(length(lineString(coords)));
      } else if (handler.type === 'polygon' && count >= 3) {
        measurement = formatAreaHectares(
          area({ type: 'Polygon', coordinates: [[...coords, coords[0]]] }),
        );
      }

      onDrawProgress({ pointCount: count, measurement });
    };

    map.on(L.Draw.Event.CREATED, handleDrawCreated);
    map.on(L.Draw.Event.DRAWSTART, handleDrawStart);
    map.on(L.Draw.Event.DRAWSTOP, handleDrawStop);
    map.on(L.Draw.Event.DRAWVERTEX, handleDrawVertex);

    return () => {
      map.off(L.Draw.Event.CREATED, handleDrawCreated);
      map.off(L.Draw.Event.DRAWSTART, handleDrawStart);
      map.off(L.Draw.Event.DRAWSTOP, handleDrawStop);
      map.off(L.Draw.Event.DRAWVERTEX, handleDrawVertex);
      if (handlerRef.current) {
        handlerRef.current.disable();
        handlerRef.current = null;
      }
      map.removeLayer(drawnItems);
      if (measureMarkerRef.current) {
        map.removeLayer(measureMarkerRef.current);
        measureMarkerRef.current = null;
      }
    };
  }, [map, onShapeDrawn, isLayerUpdateRef, onDrawProgress, initialShape]);

  useEffect(() => {
    if (handlerRef.current) {
      handlerRef.current.disable();
      handlerRef.current = null;
    }

    if (!activeTool) {
      if (onDrawProgress) onDrawProgress({ pointCount: 0, measurement: null });
      return undefined;
    }

    const handler = makeHandler(map, activeTool);
    if (!handler) return undefined;

    // leaflet-draw fires CREATED with `layerType: this.type`, so this MUST be
    // the leaflet layer type (line draws a polyline). Getting this wrong routes
    // every shape into the polyline branch of handleDrawCreated.
    handler.type = activeTool === 'line' ? 'polyline' : activeTool;
    handlerRef.current = handler;
    handler.enable();

    return () => {
      if (handlerRef.current === handler) {
        handler.disable();
        handlerRef.current = null;
      }
    };
  }, [map, activeTool, onDrawProgress]);

  // Live measurement on mouse move for every tool: lines/polygons fold the
  // cursor position into the running length/area (so the value updates while
  // you define the shape), circles/rectangles read their in-progress shape.
  useEffect(() => {
    if (!activeTool || !onDrawProgress) {
      return undefined;
    }

    const onMove = e => {
      const handler = handlerRef.current;
      if (!handler) return;

      let measurement = null;
      let pointCount = null;

      if (activeTool === 'line' || activeTool === 'polygon') {
        const markers = handler._markers || [];
        pointCount = markers.length;
        const coords = markers.map(m => {
          const ll = m.getLatLng();
          return [ll.lng, ll.lat];
        });
        if (e.latlng) coords.push([e.latlng.lng, e.latlng.lat]);

        if (activeTool === 'line' && coords.length >= 2) {
          measurement = formatLineLength(length(lineString(coords)));
        } else if (activeTool === 'polygon' && coords.length >= 3) {
          measurement = formatAreaHectares(
            area({ type: 'Polygon', coordinates: [[...coords, coords[0]]] }),
          );
        }
      } else if (activeTool === 'circle' && handler._shape?.getRadius) {
        pointCount = 1;
        measurement = formatRadiusMetres(handler._shape.getRadius());
      } else if (activeTool === 'rectangle' && handler._shape?.getBounds) {
        pointCount = 1;
        const b = handler._shape.getBounds();
        const sw = b.getSouthWest();
        const ne = b.getNorthEast();
        const ring = [
          [sw.lng, sw.lat],
          [ne.lng, sw.lat],
          [ne.lng, ne.lat],
          [sw.lng, ne.lat],
          [sw.lng, sw.lat],
        ];
        measurement = formatAreaHectares(
          area({ type: 'Polygon', coordinates: [ring] }),
        );
      }

      if (measurement != null) {
        onDrawProgress({ pointCount: pointCount ?? 1, measurement });
      }
    };

    map.on('mousemove', onMove);
    return () => map.off('mousemove', onMove);
  }, [map, activeTool, onDrawProgress]);

  useEffect(() => {
    if (drawControlRef) {
      drawControlRef.current = {
        finish: () => {
          if (handlerRef.current?._finishShape) {
            handlerRef.current._finishShape();
          } else if (handlerRef.current?.completeShape) {
            handlerRef.current.completeShape();
          }
        },
        deleteLastPoint: () => {
          if (handlerRef.current?.deleteLastVertex) {
            handlerRef.current.deleteLastVertex();
          }
        },
        cancel: () => {
          if (handlerRef.current) {
            handlerRef.current.disable();
            handlerRef.current = null;
          }
          if (onToolEnd) onToolEnd();
        },
        clearAll: () => {
          if (drawnItemsRef.current) {
            drawnItemsRef.current.clearLayers();
          }
        },
        // Geometry of the drawn search area, for renderers that redraw the map
        // themselves (the print PDF). Reading it off the live layers covers a
        // shape restored from filters as well as one just drawn. Circles are
        // reported as the box they occupy, which is what L.Circle draws.
        getShapes: () => {
          const shapes = [];

          const collect = layer => {
            if (typeof layer.getRadius === 'function' && layer.getBounds) {
              const bounds = layer.getBounds();
              shapes.push({
                kind: 'ellipse',
                bounds: [
                  [bounds.getSouth(), bounds.getWest()],
                  [bounds.getNorth(), bounds.getEast()],
                ],
              });
            } else if (typeof layer.getLatLngs === 'function') {
              shapes.push({ kind: 'ring', latLngs: layer.getLatLngs() });
            } else if (typeof layer.eachLayer === 'function') {
              layer.eachLayer(collect);
            }
          };

          drawnItemsRef.current?.eachLayer(collect);
          return shapes;
        },
      };
    }
  }, [drawControlRef, onToolEnd]);

  return null;
}
